import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LevelHeader from '../levelHeader/levelHeader';
import cx from 'classnames';
import FortuneSpin from '../fortuneSpin/FortuneSpin';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import PrizePool from './prizePool';
import { spinLevel, updateBalances, updateLocalSpinState } from 'store/authSlice';
import { showToast } from 'store/toastSlice';
import SkeletonLevelUp from './skeletonLevelUp';
import { CheckIcon, LockIcon } from 'components/icons';
import { generateIdempotencyKey } from 'utils/idempotency';
import styles from './levelUp.module.scss';

interface LevelUpProps {
  onSpinningChange?: (isSpinning: boolean) => void;
}

const LevelUp: React.FC<LevelUpProps> = ({ onSpinningChange }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [manualLevel, setManualLevel] = useState<number | null>(null);
  const [manualSubLevel, setManualSubLevel] = useState<number | null>(null);
  const { levels, subLevels, isLoading: isLevelsLoading } = useAppSelector((state) => state.level);
  const {
    user,
    isMyLevelLoading,
    isAuthenticated: rawIsAuth,
  } = useAppSelector((state) => state.auth);
  const config = useAppSelector((state) => state.site.config);
  const isAuthenticated = rawIsAuth && !config?.geo?.isBlocked;

  const [isSpinning, setIsSpinning] = useState(false);
  const [frozenLevel, setFrozenLevel] = useState<{
    tierIdx: number;
    subIdx: number;
    checks: boolean[];
  } | null>(null);
  const [winningId, setWinningId] = useState<number | null>(null);
  const [results, setResults] = useState<Record<string, number>>({});
  const [spinResponse, setSpinResponse] = useState<any>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const musicRef = React.useRef<HTMLAudioElement | null>(null);

  const updateIsSpinning = (spinning: boolean) => {
    setIsSpinning(spinning);
    onSpinningChange?.(spinning);
  };

  const userTier = user?.level?.current?.tier?.toLowerCase();
  const userTierIdx = React.useMemo(() => {
    if (!userTier || !levels.length) return 0;
    const idx = levels.findIndex((t: string) => t.toLowerCase() === userTier);
    return idx !== -1 ? idx : 0;
  }, [userTier, levels]);

  const initializedRef = React.useRef(false);

  const activeLevel =
    frozenLevel !== null ? frozenLevel.tierIdx : manualLevel !== null ? manualLevel : userTierIdx;

  const headerLevels = React.useMemo(() => {
    return levels.map((tierKey: any) => subLevels[tierKey]?.[0]?.tierName || tierKey);
  }, [levels, subLevels]);

  const userCurrentIndex = user?.level?.current?.index ?? 0;

  const currentSubLevels = (subLevels[levels[activeLevel]] || [])
    .filter((level: any) => level.index !== 0)
    .map((level: any) => ({
      ...level,
      isInactive: level.index < userCurrentIndex,
    }));

  const currentSubLevelIdx = currentSubLevels.findIndex(
    (l: any) => l.key === user?.level?.current?.key || l.status === 'current',
  );

  const firstActiveIdx = currentSubLevels.findIndex(
    (l: any) => !l.isInactive && l.wheel?.isEnabled,
  );
  const defaultSubLevel =
    currentSubLevelIdx !== -1 ? currentSubLevelIdx : firstActiveIdx !== -1 ? firstActiveIdx : 0;

  useEffect(() => {
    // Lock the UI to the user's current level on initial load.
    // This prevents background socket updates from "auto-snapping" the UI later!
    if (
      !initializedRef.current &&
      !isMyLevelLoading &&
      !isLevelsLoading &&
      currentSubLevels.length > 0
    ) {
      initializedRef.current = true;
      setManualLevel(userTierIdx);
      setManualSubLevel(defaultSubLevel);
    }
  }, [isMyLevelLoading, isLevelsLoading, currentSubLevels, userTierIdx, defaultSubLevel]);

  const activeSubLevel =
    frozenLevel !== null
      ? frozenLevel.subIdx
      : manualSubLevel !== null && currentSubLevels[manualSubLevel]
        ? manualSubLevel
        : defaultSubLevel;

  const isSpinDisabledForLevel = (level: any, overrideSpinState?: any) => {
    if (!isAuthenticated || !user?.level) return true;

    const isCurrentLevel = user?.level?.current?.key === level?.key;
    const additionalSpinCount =
      overrideSpinState?.additionalSpinCount ?? user?.level?.spin?.additionalSpinCount ?? 0;
    const hasAdditionalSpins = additionalSpinCount > 0;

    if (isCurrentLevel && hasAdditionalSpins) {
      return false;
    }

    const pendingSpinLevels =
      overrideSpinState?.pendingSpinLevels ?? user?.level?.spin?.pendingSpinLevels;
    const isPending = pendingSpinLevels?.includes(level?.key);
    if (!isPending) return true;

    if (isCurrentLevel) {
      const hasAvailableSpin = overrideSpinState?.hasAvailableSpin ?? user?.level?.hasAvailableSpin;
      return !hasAvailableSpin;
    }

    return false;
  };

  const activeSubLevelObj = subLevels[levels[activeLevel]]?.[activeSubLevel];
  const isBtnDisabled = isSpinDisabledForLevel(activeSubLevelObj) || isSpinning;

  const handleSelectLevel = (idx: number) => {
    if (isSpinning) return;
    setManualLevel(idx);
    setManualSubLevel(0); // Lock it to the first item so it doesn't float!
  };

  const handleSpin = async () => {
    if (isBtnDisabled || isSpinning) return;

    const checks = currentSubLevels.map((l: any) => {
      const isReachedOrCurrent = l.status === 'reached' || l.status === 'current';
      return isAuthenticated && isReachedOrCurrent && isSpinDisabledForLevel(l);
    });

    setFrozenLevel({ tierIdx: activeLevel, subIdx: activeSubLevel, checks });
    updateIsSpinning(true);
    setWinningId(null);

    // Play spin sound
    if (!audioRef.current) {
      audioRef.current = new Audio('/spin.mp3');
      audioRef.current.loop = false;
    }
    audioRef.current.play().catch((e) => console.log('Spin audio play failed:', e));

    // Play background music
    if (!musicRef.current) {
      musicRef.current = new Audio('/music.mp3');
      musicRef.current.loop = false;
    }
    musicRef.current.play().catch((e) => console.log('Music audio play failed:', e));

    try {
      const activeSubLevelKey = currentSubLevels[activeSubLevel]?.key;
      if (!activeSubLevelKey) {
        throw new Error('No level key available for spinning');
      }
      const idempotencyKey = generateIdempotencyKey();
      const result = await dispatch(
        spinLevel({ levelKey: activeSubLevelKey, idempotencyKey }),
      ).unwrap();
      setSpinResponse(result.data); // Store for balance update after animation

      // Use wheelPrizeId from the response as per user's example
      const prizeId = result?.data?.prize?.wheelPrizeId;

      if (prizeId) {
        setWinningId(prizeId);
        setResults((prev) => ({ ...prev, [activeSubLevelKey]: prizeId }));
      } else {
        throw new Error('No prize id in response');
      }
    } catch (err) {
      console.error('Spin failed:', err);
      updateIsSpinning(false);
      setFrozenLevel(null);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  };

  useEffect(() => {
    return () => {
      // Clean up music when leaving the component
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.currentTime = 0;
      }
    };
  }, []);
  const handleSpinEnd = () => {
    updateIsSpinning(false);
    setFrozenLevel(null);
    setWinningId(null);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    // Update balance and level info after animation completes
    if (spinResponse) {
      // ONLY update the balances from the response to avoid overwriting newer socket data!
      dispatch(updateBalances({ balances: spinResponse.balances }));

      // Locally remove the spun level from pendingSpinLevels
      const spunLevelKey = subLevels[levels[activeLevel]]?.[activeSubLevel]?.key;
      if (spunLevelKey) {
        dispatch(updateLocalSpinState(spunLevelKey));
      }

      // Use spinResponse.spin to correctly evaluate the POST-spin state,
      // avoiding stale closure state from before the spin finished.
      const firstUnspunIdx = currentSubLevels.findIndex(
        (l: any) => !isSpinDisabledForLevel(l, spinResponse.spin),
      );
      if (firstUnspunIdx !== -1) {
        setManualLevel(activeLevel);
        setManualSubLevel(firstUnspunIdx);
      } else {
        const allHaveCheckIcon = currentSubLevels.every((l: any) => {
          const isReachedOrCurrent = l.status === 'reached' || l.status === 'current';
          return isReachedOrCurrent && isSpinDisabledForLevel(l, spinResponse.spin);
        });
        if (allHaveCheckIcon && currentSubLevels.length > 0) {
          setManualLevel(activeLevel);
          setManualSubLevel(currentSubLevels.length - 1);
        }
      }

      const prize = spinResponse.prize;
      if (prize?.amount) {
        dispatch(
          showToast({
            text: t('rewards.levelUp.congratulations', { amount: prize.amount }),
            type: 'success',
          }),
        );
      }

      setSpinResponse(null);
    }
  };

  if ((isMyLevelLoading && !user?.level) || (isLevelsLoading && !levels.length)) {
    return <SkeletonLevelUp />;
  }

  return (
    <div className={styles.levelUpContainer}>
      <div className={styles.headerScrollArea}>
        <LevelHeader
          levels={headerLevels}
          activeLevel={activeLevel}
          onSelectLevel={handleSelectLevel}
        />
      </div>
      <div className={styles.wagerContainer}>
        {currentSubLevels.map((level: any, idx: number) => {
          const isReachedOrCurrent = level.status === 'reached' || level.status === 'current';
          const isAlreadySpun = isSpinDisabledForLevel(level);

          // Prevent CheckIcon blinking during socket race conditions
          // (when global config 'subLevels' updates faster than the 'user' profile)
          const isDesyncedCurrentLevel =
            level.status === 'current' && user?.level?.current?.key !== level.key;

          let showCheckBadge =
            isAuthenticated && isReachedOrCurrent && isAlreadySpun && !isDesyncedCurrentLevel;
          if (frozenLevel) {
            showCheckBadge = frozenLevel.checks[idx] ?? showCheckBadge;
          }
          showCheckBadge = showCheckBadge && !(isSpinning && activeSubLevel === idx);

          return (
            <span
              key={idx}
              className={cx(
                styles.levelWager,
                activeSubLevel === idx ? styles.active : '',
                level.status === 'not_reached' ? styles.inactive : '',
                isSpinning ? styles.disabled : '',
              )}
              onClick={() => {
                if (isSpinning) return;
                setManualLevel(activeLevel);
                setManualSubLevel(idx);
              }}
            >
              {showCheckBadge && (
                <div className={styles.forfeitBadge}>
                  <CheckIcon width="10" height="10" stroke="white" strokeWidth="2.5" />
                </div>
              )}
              <div className={styles.levelName}>{level.name}</div>
            </span>
          );
        })}
      </div>
      <div className={styles.carouselSection}>
        <FortuneSpin
          items={subLevels[levels[activeLevel]]?.[activeSubLevel]?.wheel?.prizes || []}
          isSpinning={isSpinning}
          winningId={winningId}
          stoppedId={
            !isSpinning ? results[subLevels[levels[activeLevel]]?.[activeSubLevel]?.key] : null
          }
          onSpinEnd={handleSpinEnd}
          levelName={levels[activeLevel]}
        />
      </div>

      <div className={styles.buttonContainer}>
        <button
          className={`${styles.spinButton} ${isBtnDisabled ? styles.locked : ''}`}
          onClick={handleSpin}
          disabled={isBtnDisabled}
        >
          {isBtnDisabled ? <LockIcon width="18" height="18" className={styles.lockIcon} /> : null}
          {isSpinning
            ? t('rewards.levelUp.goodLuck')
            : !isAuthenticated
              ? t('rewards.levelUp.loginToSpin')
              : user?.level?.spin?.additionalSpinCount && user?.level?.spin?.additionalSpinCount > 0
                ? t('rewards.levelUp.spinRemaining', { count: user.level.spin.additionalSpinCount })
                : t('rewards.levelUp.spin')}
        </button>
      </div>
      <PrizePool prizePoolData={subLevels[levels[activeLevel]]?.[activeSubLevel]?.wheel?.prizes} />
    </div>
  );
};

export default LevelUp;
